import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
import { useNotificationStore } from './store/notificationStore';
import { AppShell } from './components/layout/AppShell';
import { ToastContainer } from './components/ui/Toast';
import { trackPageView } from './lib/analytics';
import './styles/globals.css';

// R89 #65: surface query + mutation failures as toasts via
// notificationStore. Using getState() because we're outside
// React's component tree — QueryCache callbacks fire at the
// QueryClient level, not within a component.
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      useNotificationStore.getState().addToast({
        type: 'error',
        title: 'Something went wrong',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      useNotificationStore.getState().addToast({
        type: 'error',
        title: 'Action failed',
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// Lazy-loaded pages
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Setup2FA = lazy(() => import('./pages/Setup2FA'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Reactivate = lazy(() => import('./pages/Reactivate'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const AcceptableUse = lazy(() => import('./pages/AcceptableUse'));
const Dmca = lazy(() => import('./pages/Dmca'));
const About = lazy(() => import('./pages/About'));
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Deals = lazy(() => import('./pages/Deals'));
const DealDetail = lazy(() => import('./pages/DealDetail'));
const Payments = lazy(() => import('./pages/Payments'));
const Brands = lazy(() => import('./pages/Brands'));
const BrandDetail = lazy(() => import('./pages/BrandDetail'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Outreach = lazy(() => import('./pages/Outreach'));
const DealReviewer = lazy(() => import('./pages/DealReviewer'));
const ContractAnalyzer = lazy(() => import('./pages/ContractAnalyzer'));
const Social = lazy(() => import('./pages/Social'));
const PitchWriter = lazy(() => import('./pages/PitchWriter'));
const AIHistory = lazy(() => import('./pages/AIHistory'));
const Hub = lazy(() => import('./pages/Hub'));
const MediaKit = lazy(() => import('./pages/MediaKit'));
const Analytics = lazy(() => import('./pages/Analytics'));
const IndustryIntel = lazy(() => import('./pages/IndustryIntel'));
const Finances = lazy(() => import('./pages/Finances'));
const Legal = lazy(() => import('./pages/Legal'));
const Collabs = lazy(() => import('./pages/Collabs'));
const Goals = lazy(() => import('./pages/Goals'));
const Settings = lazy(() => import('./pages/Settings'));
const PublicMediaKit = lazy(() => import('./pages/PublicMediaKit'));

// Admin pages
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const AdminStaff = lazy(() => import('./pages/admin/AdminStaff'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminPerformance = lazy(() => import('./pages/admin/AdminPerformance'));
const AdminAIConfig = lazy(() => import('./pages/admin/AdminAIConfig'));
const AdminPurges = lazy(() => import('./pages/admin/AdminPurges'));

function LoadingFallback() {
  return (
    <div style={{ minHeight: '100vh', background: '#0A0E1A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#8B92A8', animation: 'pulse-subtle 2s ease-in-out infinite' }}>Loading...</div>
    </div>
  );
}

function AuthGuard() {
  const { profile, isLoading } = useAuthStore();
  if (isLoading) return <LoadingFallback />;
  if (!profile) return <Navigate to="/welcome" replace />;
  return <Outlet />;
}

/**
 * SubscriptionGuard — blocks access to the full app until user has an active paid subscription.
 * Allows: Home (for AI onboarding), Settings (for billing/plan selection), Admin panel.
 * Blocks: Everything else → redirects to /settings?tab=billing
 */
function SubscriptionGuard() {
  const { profile } = useAuthStore();
  if (!profile) return <Outlet />;

  // Admin users bypass subscription requirement
  if (profile.admin_role) return <Outlet />;

  // Active subscribers pass through. Status-based gate (per locked product
  // model): never-paid users (status='inactive') and post-cancel users
  // (status='canceled') are treated identically and redirected to billing.
  // past_due is a hard-block — no grace period.
  if (profile.subscription_status === 'active') {
    return <Outlet />;
  }

  return <Navigate to="/settings?tab=billing" replace />;
}

/**
 * TwoFactorGate — REQUIRED 2FA enforcement (CP4). Any authed user without 2FA
 * enabled is forced to /setup-2fa before reaching any app route (everyone,
 * admins included). Mirrors SubscriptionGuard. Runs inside AuthGuard, so a
 * missing profile is already handled upstream. /setup-2fa itself sits OUTSIDE
 * this gate, so it can never self-redirect (no loop) — the escape route.
 */
function TwoFactorGate() {
  const { profile } = useAuthStore();
  if (profile && !profile.totp_enabled) return <Navigate to="/setup-2fa" replace />;
  return <Outlet />;
}

function AuthInitializer() {
  useEffect(() => {
    const token = localStorage.getItem('cros_token');
    const store = useAuthStore.getState();
    if (!token) {
      store.setLoading(false);
      store.setInitialized(true);
    } else {
      // Token exists — try to fetch profile
      import('./api/profile').then(({ getProfile }) => {
        getProfile()
          .then((profile) => {
            store.setProfile(profile);
          })
          .catch(() => {
            // Token invalid — clear it
            localStorage.removeItem('cros_token');
            localStorage.removeItem('cros_refresh_token');
          })
          .finally(() => {
            store.setLoading(false);
            store.setInitialized(true);
          });
      });
    }
  }, []);

  return null;
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'Home',
  '/welcome': 'Welcome',
  '/login': 'Sign in',
  '/signup': 'Sign up',
  '/forgot-password': 'Forgot password',
  '/reset-password': 'Reset password',
  '/reactivate': 'Reactivate',
  '/verify-email': 'Verify email',
  '/privacy': 'Privacy',
  '/terms': 'Terms',
  '/cookies': 'Cookies',
  '/acceptable-use': 'Acceptable Use',
  '/dmca': 'DMCA',
  '/about': 'About',
  '/dashboard': 'Dashboard',
  '/deals': 'Deals',
  '/payments': 'Payments',
  '/brands': 'Brands',
  '/affiliate': 'Affiliate',
  '/outreach': 'Outreach',
  '/deal-reviewer': 'Deal Reviewer',
  '/contract-analyzer': 'Contract Analyzer',
  '/pitch-writer': 'Pitch Writer',
  '/ai-history': 'AI History',
  '/social': 'Social',
  '/hub': 'Hub',
  '/media-kit': 'Media Kit',
  '/analytics': 'Analytics',
  '/industry-intel': 'Industry Intel',
  '/finances': 'Finances',
  '/legal': 'Legal',
  '/collabs': 'Collabs',
  '/goals': 'Goals',
  '/settings': 'Settings',
};

const PREFIX_TITLES: ReadonlyArray<readonly [string, string]> = [
  ['/deals/', 'Deal Detail'],
  ['/brands/', 'Brand Detail'],
  ['/kit/', 'Public Media Kit'],
  ['/admin/users/', 'Admin — User Detail'],
  ['/admin/users', 'Admin — Users'],
  ['/admin/staff', 'Admin — Staff'],
  ['/admin/audit', 'Admin — Audit Log'],
  ['/admin/performance', 'Admin — Performance'],
  ['/admin/ai-config', 'Admin — AI Config'],
  ['/admin/purges', 'Admin — Purges'],
  ['/admin', 'Admin'],
];

function pageTitleFor(path: string): string {
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  for (const [prefix, title] of PREFIX_TITLES) {
    if (path.startsWith(prefix)) return title;
  }
  return 'CROS';
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const title = `${pageTitleFor(location.pathname)} — CROS`;
    document.title = title;
    trackPageView(location.pathname + location.search, title);
  }, [location.pathname, location.search]);
  return null;
}

function CatchAll() {
  const { profile, isLoading } = useAuthStore();
  if (isLoading) return <LoadingFallback />;
  return <Navigate to={profile ? '/' : '/welcome'} replace />;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#0A0E1A', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '2rem', fontFamily: "'Outfit', sans-serif" }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Something went wrong</h1>
          <p style={{ color: '#8B92A8', marginBottom: '1rem', maxWidth: '500px', textAlign: 'center' }}>{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/'; }} style={{ padding: '0.5rem 1.5rem', background: '#7B5CFA', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem' }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Redirect that preserves the current URL's query string.
 * react-router-dom's <Navigate> drops query params by default;
 * OAuth callbacks (Nylas) need ?code=&state= to survive the redirect
 * from /email → /hub?tab=email. The 3 Hub-consolidation routes use
 * this; the other <Navigate> calls in this file are intentional
 * control-flow redirects (auth guards) that should NOT preserve params.
 */
function RedirectWithSearch({ to }: { to: string }) {
  const { search } = useLocation();
  if (!search) return <Navigate to={to} replace />;
  const sep = to.includes('?') ? '&' : '?';
  return <Navigate to={`${to}${sep}${search.slice(1)}`} replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthInitializer />
        <RouteTracker />
        {/* Mounted above <Suspense> so toasts persist across route
            transitions and across <Routes> remounts. */}
        <ToastContainer />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Landing page — public, no auth */}
            <Route path="/welcome" element={<Landing />} />

            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reactivate" element={<Reactivate />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/kit/:slug" element={<PublicMediaKit />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/acceptable-use" element={<AcceptableUse />} />
            <Route path="/dmca" element={<Dmca />} />
            <Route path="/about" element={<About />} />

            {/* Auth required */}
            <Route element={<AuthGuard />}>
              {/* Mandatory 2FA setup — reachable while totp_enabled=false and
                  NOT behind TwoFactorGate, so it can never self-redirect (the escape). */}
              <Route path="/setup-2fa" element={<Setup2FA />} />

              {/* Everything else requires 2FA (everyone, admins included). */}
              <Route element={<TwoFactorGate />}>
              {/* Routes accessible without subscription (onboarding + billing) */}
              <Route element={<AppShell />}>
                <Route index element={<Home />} />
                <Route path="/settings" element={<Settings />} />
              </Route>

              {/* Routes requiring active paid subscription */}
              <Route element={<SubscriptionGuard />}>
                <Route element={<AppShell />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/deals" element={<Deals />} />
                  <Route path="/deals/:id" element={<DealDetail />} />
                  <Route path="/payments" element={<Payments />} />
                  <Route path="/brands" element={<Brands />} />
                  <Route path="/brands/:id" element={<BrandDetail />} />
                  <Route path="/affiliate" element={<Affiliate />} />
                  <Route path="/outreach" element={<Outreach />} />
                  <Route path="/deal-reviewer" element={<DealReviewer />} />
                  <Route path="/contract-analyzer" element={<ContractAnalyzer />} />
                  <Route path="/pitch-writer" element={<PitchWriter />} />
                  <Route path="/ai-history" element={<AIHistory />} />
                  <Route path="/social" element={<Social />} />
                  <Route path="/hub" element={<Hub />} />
                  <Route path="/email" element={<RedirectWithSearch to="/hub?tab=email" />} />
                  <Route path="/calendar" element={<RedirectWithSearch to="/hub?tab=calendar" />} />
                  <Route path="/contacts" element={<RedirectWithSearch to="/hub?tab=contacts" />} />
                  <Route path="/media-kit" element={<MediaKit />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/industry-intel" element={<IndustryIntel />} />
                  <Route path="/finances" element={<Finances />} />
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/collabs" element={<Collabs />} />
                  <Route path="/goals" element={<Goals />} />
                </Route>
              </Route>

              {/* Admin panel — stealth: non-admin users silently redirected */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="users/:id" element={<AdminUserDetail />} />
                <Route path="performance" element={<AdminPerformance />} />
                <Route path="ai-config" element={<AdminAIConfig />} />
                <Route path="purges" element={<AdminPurges />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="audit" element={<AdminAuditLog />} />
              </Route>
              </Route>
            </Route>

            {/* Catch all — logged in goes to app, logged out goes to landing */}
            <Route path="*" element={<CatchAll />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}
